/**
 * Firebase Auth + license-key session can desync (e.g. valid key in localStorage,
 * Firebase session cleared). Policy: once auth is **`ready`** (Firebase finished
 * `authStateReady()`), never show an indefinite spinner for that mismatch — send the
 * user to `/login` so they can re-authenticate. Keep this invariant covered by tests.
 */

import {
  INTENT_QUERY_KEY,
  appendIntentToPath,
  buildAuthRedirectSearch,
} from './navigation';

export type RootRedirectUser = Pick<{ emailVerified: boolean }, 'emailVerified'>;

export type RootRedirectResult =
  | { kind: 'loading' }
  | { kind: 'navigate'; to: string };

export function resolveRootRedirect(input: {
  bypassBilling: boolean;
  authReady: boolean;
  licenseAuthenticated: boolean;
  user: RootRedirectUser | null;
  searchParams: URLSearchParams;
}): RootRedirectResult {
  const {
    bypassBilling,
    authReady,
    licenseAuthenticated,
    user,
    searchParams,
  } = input;

  if (bypassBilling) {
    return { kind: 'navigate', to: appendIntentToPath('/app', searchParams) };
  }

  if (!authReady) {
    return { kind: 'loading' };
  }

  if (licenseAuthenticated) {
    if (!user) {
      return {
        kind: 'navigate',
        to: `/login${buildAuthRedirectSearch(searchParams)}`,
      };
    }
    if (!user.emailVerified) {
      const qs = new URLSearchParams();
      const intent = searchParams.get(INTENT_QUERY_KEY);
      if (intent) qs.set(INTENT_QUERY_KEY, intent);
      qs.set('return', '/app');
      qs.set('verify', '1');
      return { kind: 'navigate', to: `/login?${qs.toString()}` };
    }
    return {
      kind: 'navigate',
      to: appendIntentToPath('/app', searchParams),
    };
  }

  if (user) {
    return {
      kind: 'navigate',
      to: appendIntentToPath('/app', searchParams),
    };
  }

  return {
    kind: 'navigate',
    to: `/login${buildAuthRedirectSearch(searchParams)}`,
  };
}
